/**
 * @fileoverview Regios DOPP integration for Liquid-rendered product cards (carousels, and any
 * other server-rendered product grid using `shared-product-card`), plus the PDP's own price
 * element.
 *
 * These surfaces are rendered server-side first, so the regular price is genuinely visible in
 * the initial HTML before this script resolves the discount. This is a progressive enhancement:
 * it never blocks rendering, and if the DOPP API doesn't show up (or a given calculation
 * rejects), the server-rendered regular price stays exactly as-is.
 *
 * Debugging: DOPP failures are silent by design, so set `localStorage.setItem('dopp:debug', '1')`
 * or add `?dopp_debug=1` to the URL to see verbose `[DOPP]` logs for every card. A handful of
 * always-on `console.warn` calls also surface the most common misconfigurations (API never
 * initialized, a card missing the data it needs, a rejected calculation).
 */
import { waitForDoppApi, pickLocalizedDoppMessage } from './dopp-shared.js';

const DOPP_INIT_TIMEOUT_MS = 2000;
const PROCESSED_ATTR = 'doppProcessed';

/** @returns {boolean} whether verbose `[DOPP]` debug logging is enabled for this session. */
function isDebugEnabled() {
  try {
    if (window.localStorage && window.localStorage.getItem('dopp:debug') === '1') return true;
  } catch {
    // localStorage can throw in locked-down/private-browsing contexts; treat as disabled.
  }
  try {
    return new URLSearchParams(window.location.search).get('dopp_debug') === '1';
  } catch {
    return false;
  }
}

/** Verbose debug logging, opt-in only (see `isDebugEnabled`). */
function doppLog(...args) {
  if (!isDebugEnabled()) return;
  console.debug('[DOPP]', ...args);
}

/** Always-on warning for misconfigurations a merchant/developer should notice. */
function doppWarn(...args) {
  console.warn('[DOPP]', ...args);
}

/**
 * @param {string | null | undefined} value
 * @param {unknown} fallback
 * @returns {unknown}
 */
function safeJsonParse(value, fallback) {
  if (!value) return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

/** Strips HTML tags and normalizes whitespace/case, for comparing display text regardless of markup. */
function toComparableText(value) {
  return String(value || '')
    .replace(/<[^>]*>/g, '')
    .trim()
    .toLowerCase();
}

/** Escapes a plain-text string for safe insertion via innerHTML. */
function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = String(value || '');
  return div.innerHTML;
}

/**
 * Minimal allowlist sanitizer for HTML strings returned by the DOPP API (badge/description).
 * This file ships as a raw theme asset outside the Vite bundle, so pulling in a full sanitizer
 * (e.g. DOMPurify, used on the Preact side) isn't wired up here yet — this strips the highest-risk
 * vectors (script tags, inline event handlers, javascript: URLs) as a stopgap.
 */
function sanitizeHtml(html) {
  if (!html) return '';
  return String(html)
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/\son\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]*)/gi, '')
    .replace(/(href|src)\s*=\s*(?:"javascript:[^"]*"|'javascript:[^']*')/gi, '');
}

/**
 * DOPP's discount description/message is a live, computed version of shared-product-card-label.liquid
 * / product-label.liquid's own "Promotional Message" priority tier — not a separate, higher
 * priority tier of its own. It's therefore gated by the exact same conditions that tier already
 * respects: Sold Out, Preorder, Coming Soon, Excluded-from-Promotions, and Online Exclusive
 * (priorities 1-6) all still take precedence and must not be overwritten by it. The product's own
 * (non-empty) Promotional Message also takes precedence over DOPP's.
 *
 * @param {DOMStringMap} dataset
 * @param {string} datasetPrefix lets this same check work for both `data-dopp-*` (carousel) and
 *   `data-dopp-pdp-*` (PDP) attribute naming.
 * @returns {boolean}
 */
function isBlockedFromDoppPromotionalMessage(dataset, datasetPrefix) {
  const get = (suffix) => dataset[datasetPrefix + suffix];

  const available = get('Available') !== 'false';
  const isPreOrder = get('MfPreOrder') === 'true';
  const isComingSoon = get('MfComingSoon') === 'true';
  const isOnlineExclusive = get('MfOnlineExclusive') === 'true';
  const hasOwnPromoMessage = !!get('PromoMessage');
  const tags = safeJsonParse(get('Tags'), []);
  const hasExcludedFromPromotions =
    Array.isArray(tags) && tags.some((tag) => String(tag).toLowerCase().indexOf('excludedfrompromotions') >= 0);

  if (!available && !isComingSoon && !isPreOrder) return true; // Sold Out
  if (isPreOrder) return true;
  if (isComingSoon) return true;
  if (hasExcludedFromPromotions) return true;
  if (isOnlineExclusive) return true;
  if (hasOwnPromoMessage) return true;
  return false;
}

/**
 * Product metafields a promotion's eligibility conditions may key off, mapped to their real
 * Shopify namespace/key. `non_returnable_type` in particular is the exact field this theme uses
 * to compute the "Final Sale"/"Last Chance" fallback label — if a promotion is configured to
 * exclude Final Sale items via this metafield, Regios needs it to evaluate that condition.
 *
 * @param {Record<string, string | undefined>} dataset
 * @returns {Array<{ namespace: string, key: string, value: string }>}
 */
function toDoppMetafields(dataset) {
  const country = dataset.doppCountry || 'US';
  const metafields = [];

  const push = (namespace, key, value) => {
    if (typeof value === 'string' && value.length > 0) {
      metafields.push({ namespace, key, value });
    }
  };

  push('custom', 'non_returnable_type', dataset.doppMfNonReturnableType);
  push('custom', 'category', dataset.doppMfCategory);
  push('custom', 'gender', dataset.doppMfGender);
  push('custom', 'color', dataset.doppMfColor);
  push('custom', 'silhouette', dataset.doppMfSilhouette);
  push('pim', 'onlineExclusive', dataset.doppMfOnlineExclusive);
  push('pim', `clearance${country}`, dataset.doppMfClearance);
  push('pim', `comingSoon${country}`, dataset.doppMfComingSoon);
  push('pim', `preOrder${country}`, dataset.doppMfPreOrder);
  push('pim', `isNewArrival${country}`, dataset.doppMfNewArrival);

  return metafields;
}

/**
 * `window.globalProductData` (assets/product-card-element-utils.js) caches raw GraphQL nodes for
 * every companion/color-swatch product on the page (`PRODUCT_CARD_QUERY`,
 * assets/graphql-queries.js), keyed by full product gid. Its `metafields` selection only returns
 * `{ key, value }` — no `namespace` — so it's mapped back here from the same fixed set of
 * namespace/key pairs the query itself requests.
 */
const GLOBAL_PRODUCT_DATA_METAFIELD_NAMESPACES = {
  highlights: 'custom',
  media_gallery_assets: 'custom',
  low_stock_value: 'custom',
  publish_date_us: 'custom',
  publish_date_ca: 'custom',
  non_returnable_type: 'custom',
  silhouette: 'custom',
  color: 'custom',
  gender: 'custom',
  category: 'custom',
  promotional_message_us_en: 'custom',
  promotional_message_ca_en: 'custom',
  promotional_message_ca_fr: 'custom',
  promo_applied_message_us_en: 'custom',
  promo_applied_message_ca_en: 'custom',
  promo_applied_message_ca_fr: 'custom',
  collaboration: 'pim',
  heelType: 'pim',
  onlineExclusive: 'pim',
  comingSoonUS: 'pim',
  comingSoonCA: 'pim',
  preOrderUS: 'pim',
  preOrderCA: 'pim',
  bestseller_us: 'pim',
  bestseller_ca: 'pim',
  trending_us: 'pim',
  trending_ca: 'pim',
  isNewArrivalUS: 'pim',
  isNewArrivalCA: 'pim',
  socialConfidenceUS: 'pim',
  socialConfidenceCA: 'pim',
  sellingFastUS: 'pim',
  sellingFastCA: 'pim',
};

/**
 * Builds a DOPP payload for a companion/color-swatch product from `window.globalProductData`'s
 * cache, rather than reusing this card's static `data-dopp-*` attributes — those describe the
 * product Liquid originally rendered, which is a *different* product than whatever color the
 * shopper just swatched to. Recalculating with the wrong product's data would show a
 * plausible-looking but incorrect discount, which is worse than the clobbered regular price.
 *
 * `PRODUCT_CARD_QUERY` (assets/graphql-queries.js) fetches `collections`/`vendor` specifically
 * for this — without them, a swatch-selected product's collection-scoped promotions would
 * silently evaluate as not eligible even when the product genuinely is.
 *
 * @param {Record<string, unknown> | null | undefined} rawNode
 * @returns {import('../bloomreach/types/dopp').DoppProduct | null}
 */
function toDoppProductFromGlobalProductData(rawNode) {
  if (!rawNode || !rawNode.id) {
    doppLog('toDoppProductFromGlobalProductData: no cached product data.', rawNode);
    return null;
  }

  const productId = Number(String(rawNode.id).replace(/^gid:\/\/shopify\/Product\//i, ''));
  if (!productId) return null;

  const regularAmount =
    rawNode.priceRange && rawNode.priceRange.maxVariantPrice
      ? parseFloat(rawNode.priceRange.maxVariantPrice.amount)
      : NaN;
  if (!Number.isFinite(regularAmount)) {
    doppLog(`toDoppProductFromGlobalProductData: product ${productId} has no usable priceRange.`);
    return null;
  }

  const compareAmount =
    rawNode.compareAtPriceRange && rawNode.compareAtPriceRange.maxVariantPrice
      ? parseFloat(rawNode.compareAtPriceRange.maxVariantPrice.amount)
      : NaN;

  const metafields = [];
  (rawNode.metafields || []).forEach((metafield) => {
    if (!metafield || !metafield.key || metafield.value == null) return;
    const namespace = GLOBAL_PRODUCT_DATA_METAFIELD_NAMESPACES[metafield.key];
    if (!namespace) return;
    metafields.push({ namespace, key: metafield.key, value: String(metafield.value) });
  });

  const collectionIds =
    rawNode.collections && Array.isArray(rawNode.collections.nodes)
      ? rawNode.collections.nodes
          .map((node) => Number(String(node.id).replace(/^gid:\/\/shopify\/Collection\//i, '')))
          .filter((id) => Number.isFinite(id) && id > 0)
      : [];

  if (collectionIds.length === 0) {
    doppLog(
      `toDoppProductFromGlobalProductData: product ${productId} has no collectionIds — collection-scoped promotions will silently evaluate as not eligible.`,
    );
  }

  return {
    productId,
    variantId: productId,
    regularPriceInCents: Math.round(regularAmount * 100),
    compareAtPriceInCents:
      Number.isFinite(compareAmount) && compareAmount > 0 ? Math.round(compareAmount * 100) : undefined,
    tags: Array.isArray(rawNode.tags) ? rawNode.tags : undefined,
    vendor: rawNode.vendor || undefined,
    collectionIds: collectionIds.length > 0 ? collectionIds : undefined,
    handle: rawNode.handle || undefined,
    url: rawNode.handle ? `/products/${rawNode.handle}` : undefined,
    metafields: metafields.length > 0 ? metafields : undefined,
  };
}

/**
 * Reads the canonical set of DOPP fields off a `shared-product-card` element's dataset.
 *
 * @param {Element} el
 * @returns {import('../bloomreach/types/dopp').DoppProduct | null}
 */
function toDoppProduct(el) {
  const dataset = el.dataset;
  const productId = Number(dataset.doppProductId);
  if (!productId) {
    doppLog('toDoppProduct: skipped element — missing/invalid data-dopp-product-id.', el);
    return null;
  }

  const regularPriceInCents = Number(dataset.doppPriceCents);
  if (!Number.isFinite(regularPriceInCents)) {
    doppLog(`toDoppProduct: skipped product ${productId} — missing/invalid data-dopp-price-cents.`);
    return null;
  }

  if (!dataset.doppCollectionIds) {
    doppLog(
      `toDoppProduct: product ${productId} has no data-dopp-collection-ids — collection-scoped promotions will silently evaluate as not eligible.`,
    );
  }

  return {
    productId,
    variantId: Number(dataset.doppVariantId) || productId,
    regularPriceInCents,
    compareAtPriceInCents: dataset.doppCompareAtCents ? Number(dataset.doppCompareAtCents) : undefined,
    collectionIds: safeJsonParse(dataset.doppCollectionIds, []),
    tags: safeJsonParse(dataset.doppTags, []),
    vendor: dataset.doppVendor || undefined,
    handle: dataset.doppHandle || undefined,
    url: dataset.doppUrl || undefined,
    metafields: toDoppMetafields(dataset),
  };
}

/**
 * Reads the canonical set of DOPP fields off the PDP's own `.price` element (rendered by
 * snippets/price.liquid with `use_variant: true`). Same shape as `toDoppProduct` above, but with
 * `data-dopp-pdp-*` attribute names so this never collides with `shared-product-card` cards that
 * might also be present on the same page (e.g. "You may also like" recommendations).
 *
 * @param {Element} el
 * @returns {import('../bloomreach/types/dopp').DoppProduct | null}
 */
function toDoppProductPdp(el) {
  const dataset = el.dataset;
  const productId = Number(dataset.doppPdpProductId);
  if (!productId) {
    doppLog('toDoppProductPdp: skipped element — missing/invalid data-dopp-pdp-product-id.', el);
    return null;
  }

  const regularPriceInCents = Number(dataset.doppPdpPriceCents);
  if (!Number.isFinite(regularPriceInCents)) {
    doppLog(`toDoppProductPdp: skipped product ${productId} — missing/invalid data-dopp-pdp-price-cents.`);
    return null;
  }

  if (!dataset.doppPdpCollectionIds) {
    doppLog(
      `toDoppProductPdp: product ${productId} has no data-dopp-pdp-collection-ids — collection-scoped promotions will silently evaluate as not eligible.`,
    );
  }

  return {
    productId,
    variantId: Number(dataset.doppPdpVariantId) || productId,
    regularPriceInCents,
    compareAtPriceInCents: dataset.doppPdpCompareAtCents ? Number(dataset.doppPdpCompareAtCents) : undefined,
    collectionIds: safeJsonParse(dataset.doppPdpCollectionIds, []),
    tags: safeJsonParse(dataset.doppPdpTags, []),
    vendor: dataset.doppPdpVendor || undefined,
    handle: dataset.doppPdpHandle || undefined,
    url: dataset.doppPdpUrl || undefined,
    metafields: toDoppMetafields({
      doppCountry: dataset.doppPdpCountry,
      doppMfNonReturnableType: dataset.doppPdpMfNonReturnableType,
      doppMfCategory: dataset.doppPdpMfCategory,
      doppMfGender: dataset.doppPdpMfGender,
      doppMfColor: dataset.doppPdpMfColor,
      doppMfSilhouette: dataset.doppPdpMfSilhouette,
      doppMfOnlineExclusive: dataset.doppPdpMfOnlineExclusive,
      doppMfClearance: dataset.doppPdpMfClearance,
      doppMfComingSoon: dataset.doppPdpMfComingSoon,
      doppMfPreOrder: dataset.doppPdpMfPreOrder,
      doppMfNewArrival: dataset.doppPdpMfNewArrival,
    }),
  };
}

/** @param {import('../bloomreach/types/dopp').DoppCalculateDiscountedPricesResult} result */
function getDiscountBadge(result) {
  return `${Number(((1 - result.salePrice.amount / result.regularPrice.amount) * 100).toFixed(1))}%`;
}

/**
 * Applies a DOPP calculation result to a `shared-product-card`'s price and label markup.
 *
 * @param {Element} el
 * @param {import('../bloomreach/types/dopp').DoppProduct} product
 * @param {import('../bloomreach/types/dopp').DoppCalculateDiscountedPricesResult} result
 */
function applyResult(el, product, result) {
  const productId = product.productId;
  const hasDiscount = result.salePrice.amount < result.regularPrice.amount;
  doppLog(`applyResult: product ${productId}`, {
    hasDiscount,
    salePrice: result.salePrice,
    regularPrice: result.regularPrice,
    discountMessage: result.discountMessage,
    hasDescription: !!(result.description && result.description.html),
  });

  const priceEl = el.querySelector(
    '.c-product-price__formatted-price:not(.c-product-price__formatted-price--discounted)',
  );
  let comparePriceEl = el.querySelector('.c-product-price__formatted-price--discounted');
  const priceContainer = el.querySelector('.c-product-price');

  if (hasDiscount) {
    if (priceEl) {
      priceEl.textContent = result.salePrice.formatted;
    } else {
      doppWarn(`applyResult: product ${productId} has a discount, but no price element was found to update.`);
    }
    if (priceContainer) priceContainer.classList.add('c-product-price--has-discount');

    if (!comparePriceEl && priceContainer) {
      comparePriceEl = document.createElement('span');
      comparePriceEl.className =
        'c-product-price__formatted-price c-product-price__formatted-price--discounted fixed-utility-s';
      priceContainer.appendChild(comparePriceEl);
    }
    const isStacked = product.compareAtPriceInCents > result.regularPrice.amount * 100;
    if (isStacked && priceContainer && priceEl && comparePriceEl) {
      // Preserve the server-rendered compare-at price; DOPP's regular price is the markdown.
      const previousPrices = document.createElement('div');
      previousPrices.className = 'c-product-price__previous-prices';
      const regularLabel = document.createElement('span');
      regularLabel.className = 'visually-hidden visually-hidden--inline';
      regularLabel.textContent = el.dataset.doppTRegularPrice;
      const saleLabel = regularLabel.cloneNode();
      saleLabel.textContent = el.dataset.doppTSalePrice;
      const previousSalePrice = document.createElement('s');
      previousSalePrice.className = priceEl.className.replace('fixed-utility-m', 'fixed-utility-s');
      previousSalePrice.textContent = result.regularPrice.formatted;
      previousPrices.append(saleLabel, previousSalePrice, ' ', regularLabel, comparePriceEl);
      priceContainer.prepend(previousPrices);
      priceContainer.classList.add('c-product-price--stacked-discount');
    } else if (comparePriceEl) {
      comparePriceEl.textContent = result.regularPrice.formatted;
    }
    if (priceContainer && el.dataset.doppEnableBadges === 'true') {
      let badge = priceContainer.querySelector('.c-product-price__badge');
      if (!badge) {
        const wrapper = document.createElement('span');
        wrapper.className = 'c-product-price__badge__wrapper c-product-price__badge__wrapper--sale';
        badge = document.createElement('span');
        badge.className = 'c-product-price__badge fixed-utility-s-em';
        wrapper.appendChild(badge);
        priceContainer.appendChild(wrapper);
      }
      badge.textContent = isStacked ? el.dataset.doppTSaleBadge : `${getDiscountBadge(result)} ${el.dataset.doppTOff}`;
      if (isStacked) {
        priceContainer.querySelector('.c-product-price__previous-prices')?.appendChild(badge.parentElement);
      }
    }
  }

  // The discount's "title"/description is a live Promotional Message — it substitutes for
  // whatever shared-product-card-label.liquid's own Promotional Message tier would show, so
  // Sold Out/Preorder/Coming Soon/Excluded-from-Promotions/Online-Exclusive (the tiers above it)
  // must still take precedence and are left untouched.
  let localizedMessage = pickLocalizedDoppMessage(result.discountMessage);
  const blockedFromProductLabel = isBlockedFromDoppPromotionalMessage(el.dataset, 'dopp');

  if (blockedFromProductLabel) {
    localizedMessage = '';
    if (result.description && result.description.html) {
      doppLog(
        `applyResult: product ${productId} has a discount description, but a higher-priority label ` +
          '(sold out/preorder/coming soon/excluded from promotions/online exclusive/own promo message) takes precedence — leaving it alone.',
      );
    }
  }

  if (!blockedFromProductLabel && result.description && result.description.html) {
    const localizedDescriptionHtml = sanitizeHtml(pickLocalizedDoppMessage(result.description.html));

    if (localizedMessage && toComparableText(localizedDescriptionHtml) === toComparableText(localizedMessage)) {
      doppLog(`applyResult: product ${productId} has a duplicate description/price message, suppressing price label.`);
      localizedMessage = '';
    }

    const productLabel = el.querySelector('[data-replaceable-card-product-label]');
    if (productLabel) {
      let labelContent = productLabel.querySelector('.c-product-tile__product-label-content');
      if (!labelContent) {
        labelContent = document.createElement('span');
        productLabel.appendChild(labelContent);
      }
      // Replace the full class list (not classList.add) — this span may already carry a
      // different color class from whatever label shared-product-card-label.liquid computed
      // server-side, and DOPP's description replaces it entirely as the Promotional Message
      // tier, matching that tier's own `c-product-text-color--regular` (Preact's ProductLabel,
      // bloomreach/components/product-card/product-label.tsx, uses the same class for this).
      labelContent.className = 'c-product-tile__product-label-content fixed-utility-s c-product-text-color--regular';
      labelContent.innerHTML = localizedDescriptionHtml;
    } else {
      doppLog(
        `applyResult: product ${productId} has a discount description, but no [data-replaceable-card-product-label] found.`,
      );
    }
  }

  if (localizedMessage) {
    let priceLabel = el.querySelector('[data-replaceable-card-price-label]');
    if (!priceLabel) {
      // shared-product-card-label.liquid only renders this wrapper when there was already a
      // server-computed price label — create it on demand, matching Preact's PriceLabel
      // (bloomreach/components/product-card/price-label.tsx), which always renders one when
      // `priceInfo.label` is set regardless of whether anything existed before.
      const priceItem = el.querySelector('.c-product-tile-info__item--price');
      if (priceItem) {
        priceLabel = document.createElement('div');
        priceLabel.className = 'c-product-tile__price-label';
        priceLabel.setAttribute('data-replaceable-card-price-label', '');
        priceItem.insertAdjacentElement('afterend', priceLabel);
      }
    }
    if (priceLabel) {
      let content = priceLabel.querySelector('.c-product-tile__price-label-content');
      if (!content) {
        content = document.createElement('span');
        priceLabel.appendChild(content);
      }
      // Replace the full class list — matches Preact's PriceLabel, which always uses
      // `c-product-text-color--urgency` for a DOPP-driven promo message rather than whatever
      // color class (e.g. "regular") a pre-existing server-rendered label happened to use.
      content.className = 'c-product-tile__price-label-content fixed-utility-s c-product-text-color--urgency';
      content.textContent = localizedMessage;
    }
  }
}

/**
 * Calculates and applies the discounted price for a single card. Fails silently by design —
 * the server-rendered regular price is left untouched on any error.
 *
 * @param {import('../bloomreach/types/dopp').DoppPublicApiV0} api
 * @param {Element} el
 */
async function enhance(api, el) {
  if (el.dataset[PROCESSED_ATTR]) return;
  el.dataset[PROCESSED_ATTR] = 'true';

  const doppProduct = toDoppProduct(el);
  if (!doppProduct) return;

  try {
    const result = await api.calculateDiscountedPrices(doppProduct);
    applyResult(el, doppProduct, result);
  } catch (error) {
    doppWarn(`enhance: calculateDiscountedPrices rejected for product ${doppProduct.productId}.`, error);
    // Leave the regular price visible; never block rendering on this.
  }
}

/**
 * Writes DOPP's discount copy into this theme's own PDP label slot (`snippets/product-label.liquid`,
 * rendered inside `.product__descriptors` — the PDP equivalent of the grid's product-label/"Last
 * Chance" priority chain), rather than inventing a new element. Overwrites whatever that snippet
 * already computed server-side (promo message, "Final Sale", online exclusive, etc.), mirroring
 * how the grid's DOPP description takes priority over its computed label.
 *
 * Production renders both a primary label (e.g. "Limited time only") and a secondary message
 * (e.g. "- Extra 30% off at cart" — description/discountMessage) together; this mirrors that
 * structure rather than only writing the secondary one.
 *
 * There's only one product on a PDP, so a page-level lookup is safe. Creates the `.product-label`
 * container (and whichever spans are missing) on demand if the snippet didn't render one at all.
 *
 * @param {string} primaryHtml
 * @param {string} secondaryHtml
 */
function updatePdpProductLabel(primaryHtml, secondaryHtml) {
  const descriptors = document.querySelector('.product__descriptors');
  if (!descriptors) {
    doppLog('updatePdpProductLabel: no .product__descriptors found on this page.');
    return;
  }

  let container = descriptors.querySelector('.product-label');
  if (!container) {
    container = document.createElement('div');
    container.className = 'product-label';
    descriptors.insertBefore(container, descriptors.firstChild);
  }

  if (primaryHtml) {
    let primary = container.querySelector('.product-label_primary-label');
    if (!primary) {
      primary = document.createElement('span');
      container.insertBefore(primary, container.firstChild);
    }
    // Replace the full class list — this span may already exist with a different
    // primary_label_type-specific class from product-label.liquid (e.g. "final_sale",
    // "inventory"), and DOPP's badge takes priority over it entirely, not stacking on top.
    primary.className =
      'product-label_primary-label fixed-utility-m-em product-label_primary-label-sale product-label_primary-label-promo';
    primary.innerHTML = primaryHtml;
  }

  if (secondaryHtml) {
    const primaryEl = container.querySelector('.product-label_primary-label');
    const prefix = primaryEl && primaryEl.textContent.trim() ? ' - ' : '';

    let secondary = container.querySelector('.product-label_secondary-label');
    if (!secondary) {
      secondary = document.createElement('span');
      container.appendChild(secondary);
    }
    // Same reasoning as above — replace the full class list rather than preserving whatever
    // secondary_label_type-specific class (e.g. "problem_child", "return") was already there.
    secondary.className =
      'product-label_secondary-label fixed-utility-m product-label_secondary-label-promotion_message';
    secondary.innerHTML = prefix + secondaryHtml;
  }
}

/**
 * Applies a DOPP result to the PDP's own price element. Unlike the grid/carousel card markup,
 * a product with no existing markdown renders a different DOM shape (no strikethrough element
 * at all — see snippets/price.liquid) — so this creates that element on demand when DOPP finds
 * a discount that Liquid didn't already know about.
 *
 * @param {Element} el
 * @param {number} productId
 * @param {import('../bloomreach/types/dopp').DoppCalculateDiscountedPricesResult} result
 */
function applyPdpResult(el, productId, result) {
  const hasDiscount = result.salePrice.amount < result.regularPrice.amount;
  doppLog(`applyPdpResult: product ${productId}`, {
    hasDiscount,
    salePrice: result.salePrice,
    regularPrice: result.regularPrice,
    discountMessage: result.discountMessage,
    hasDescription: !!(result.description && result.description.html),
  });

  if (hasDiscount) {
    const wrapperEl = el.querySelector('.price__wrapper');
    const mainPriceEl = el.querySelector('#main-price');
    if (!wrapperEl || !mainPriceEl) {
      doppWarn(`applyPdpResult: product ${productId} has a discount, but the price markup was not found.`);
    } else {
      const ds = el.dataset;
      const isStacked = Number(ds.doppPdpCompareAtCents) > result.regularPrice.amount * 100;
      const originalComparePrice = el.querySelector('.price-item--regular')?.textContent.trim();
      const regularLabel = `<span class="visually-hidden visually-hidden--inline">${escapeHtml(ds.doppPdpTRegularPrice)}</span>`;
      const saleLabel = `<span class="visually-hidden visually-hidden--inline">${escapeHtml(ds.doppPdpTSalePrice)}</span>`;
      const previousSalePrice = `<s class="${isStacked ? escapeHtml(mainPriceEl.className.replace('fixed-heading-s', 'fluid-speciality-markdown-price-m')) : 'price-item price-item--regular fluid-speciality-markdown-price-m'}">${escapeHtml(result.regularPrice.formatted)}</s>`;
      const badgeMarkup =
        ds.doppPdpEnableBadges === 'true'
          ? '<span class="price__badge__wrapper price__badge__wrapper--sale">' +
            `<span class="price__badge fixed-utility-s-em">${isStacked ? escapeHtml(ds.doppPdpTSaleBadge) : `${getDiscountBadge(result)} ${escapeHtml(ds.doppPdpTOff)}`}</span></span>`
          : '';
      wrapperEl.innerHTML =
        (isStacked
          ? '<div class="price__previous-prices">' +
            saleLabel +
            previousSalePrice +
            regularLabel +
            `<s class="price-item price-item--regular fluid-speciality-markdown-price-m">${escapeHtml(originalComparePrice)}</s>` +
            badgeMarkup +
            '</div>'
          : '') +
        saleLabel +
        `<span id="main-price" class="price-item price-item--sale fixed-heading-s" data-product-price="${escapeHtml(result.salePrice.formatted)}">${escapeHtml(result.salePrice.formatted)}</span>` +
        (!isStacked ? regularLabel + previousSalePrice : '') +
        (!isStacked ? badgeMarkup : '');
      el.classList.remove('price--no-compare');
      el.classList.add('price--on-sale');
      el.classList.toggle('price--stacked-discount', isStacked);
    }

    // `add-to-cart-button` reads `data-product-price` synchronously, before this async DOPP
    // calculation resolves — re-sync its label now.
    const addToCartButton = document.querySelector('add-to-cart-button');
    if (addToCartButton && typeof addToCartButton.setDefaultTextValue === 'function') {
      addToCartButton.setDefaultTextValue();
    }
  }

  // The discount's title/description is a live Promotional Message — it substitutes for
  // whatever product-label.liquid's own Promotional Message tier would show, so Sold
  // Out/Preorder/Coming Soon/Excluded-from-Promotions/Online-Exclusive (the tiers above it)
  // still take precedence and are left untouched.
  if (isBlockedFromDoppPromotionalMessage(el.dataset, 'doppPdp')) {
    doppLog(
      `applyPdpResult: product ${productId} has a discount description/message, but a higher-priority label ` +
        '(sold out/preorder/coming soon/excluded from promotions/online exclusive/own promo message) takes precedence — leaving it alone.',
    );
    return;
  }

  let localizedMessage = pickLocalizedDoppMessage(result.discountMessage);
  const localizedDescriptionHtml = result.description?.html
    ? sanitizeHtml(pickLocalizedDoppMessage(result.description.html))
    : '';

  if (
    localizedDescriptionHtml &&
    localizedMessage &&
    toComparableText(localizedDescriptionHtml) === toComparableText(localizedMessage)
  ) {
    localizedMessage = '';
  }

  // Uses the theme's own "Limited time only" translation (products.product.labels.limited,
  // threaded in via data-dopp-pdp-t-limited) rather than DOPP's `badge.html` — that field is a
  // small self-styled visual badge (it carries its own `css`), not label copy, and isn't
  // confirmed to contain text appropriate for this slot.
  const primaryHtml = hasDiscount ? escapeHtml(el.dataset.doppPdpTLimited) : '';
  const secondaryHtml = localizedDescriptionHtml || (localizedMessage ? sanitizeHtml(localizedMessage) : '');

  if (primaryHtml || secondaryHtml) {
    updatePdpProductLabel(primaryHtml, secondaryHtml);
  }
}

/**
 * Calculates and applies the discounted price for the PDP's own price element. Fails silently
 * by design — the server-rendered regular price is left untouched on any error.
 *
 * @param {import('../bloomreach/types/dopp').DoppPublicApiV0} api
 * @param {Element} el
 */
async function enhancePdp(api, el) {
  if (el.dataset[PROCESSED_ATTR]) return;
  el.dataset[PROCESSED_ATTR] = 'true';

  const doppProduct = toDoppProductPdp(el);
  if (!doppProduct) return;

  try {
    const result = await api.calculateDiscountedPrices(doppProduct);
    applyPdpResult(el, doppProduct.productId, result);
  } catch (error) {
    doppWarn(`enhancePdp: calculateDiscountedPrices rejected for product ${doppProduct.productId}.`, error);
    // Leave the regular price visible; never block rendering on this.
  }
}

/**
 * Enhances every not-yet-processed `shared-product-card` and PDP price element currently in `root`.
 *
 * @param {ParentNode} [root]
 */
async function enhanceAll(root) {
  const scope = root || document;
  const cards = scope.querySelectorAll('shared-product-card[data-dopp-product-id]:not([data-dopp-processed])');
  const pdpPrices = scope.querySelectorAll('.price[data-dopp-pdp-product-id]:not([data-dopp-processed])');
  if (cards.length === 0 && pdpPrices.length === 0) return;

  doppLog(`enhanceAll: found ${cards.length} unprocessed card(s), ${pdpPrices.length} unprocessed PDP price(s).`);

  const api = await waitForDoppApi(DOPP_INIT_TIMEOUT_MS);
  if (!api) {
    doppWarn(
      `enhanceAll: API did not initialize within ${DOPP_INIT_TIMEOUT_MS}ms. Falling back to regular prices. ` +
        'Check that the Regios app embed is enabled for this theme.',
    );
    return;
  }
  doppLog('enhanceAll: API initialized.');

  cards.forEach((el) => void enhance(api, el));
  pdpPrices.forEach((el) => void enhancePdp(api, el));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => void enhanceAll(document));
} else {
  void enhanceAll(document);
}

// Re-run for cards appended after the initial paint (e.g. carousel lazy-loading, "Load more").
window.addEventListener('regios-dopp:collection-page:new-items', () => {
  doppLog('enhanceAll: triggered by regios-dopp:collection-page:new-items.');
  void enhanceAll(document);
});

// The PDP's price block is fully re-rendered server-side on variant change (see
// assets/product-info.js's `updateSourceFromDestination('price')`), which replaces the old
// `.price` node — including whatever this script previously wrote into it — with a fresh,
// non-DOPP-adjusted one for the new variant. Re-enhance immediately rather than waiting for the
// MutationObserver fallback below, so the discounted price doesn't flash the regular price.
if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) {
  window.subscribe(window.PUB_SUB_EVENTS.variantChange, () => {
    doppLog('enhanceAll: triggered by PUB_SUB_EVENTS.variantChange.');
    void enhanceAll(document);
  });
}

/**
 * The `shared-product-card` custom element (assets/product-card-element.js) owns its own
 * swatch-change pipeline: clicking a companion/color swatch calls its debounced `updateCard()`,
 * which rewrites price/badge/label markup from Shopify data with zero DOPP awareness — silently
 * clobbering whatever this script wrote in (the same class of bug fixed on the Preact grid side,
 * see bloomreach/components/product-card/product-card-content.tsx). That element dispatches no
 * event we can hook into — and its own `handleSwatchClick` calls `event.preventDefault()` on the
 * swatch click, which cancels the browser's native radio-check-toggle, so the native `change`
 * event this file previously listened for never actually fires. The event that really drives
 * swatch selection is `click` — listen for that instead, wait for the debounce inside
 * `product-card-element.js`'s `setupEventListeners` to finish, then recalculate for whichever
 * product is now actually selected.
 */
document.addEventListener('click', (event) => {
  const swatch = event.target.closest && event.target.closest('.color-selector__radio');
  if (!swatch) return;

  const card = swatch.closest('shared-product-card[data-dopp-product-id]');
  if (!card) return;

  const swatchProductGid = swatch.dataset.productId;

  setTimeout(async () => {
    // This card's `data-dopp-*` attributes describe the product Liquid originally rendered —
    // not necessarily the one now selected, if the shopper just switched color. Recalculate
    // from the swatch's own cached product data rather than reusing those stale attributes.
    const rawNode =
      swatchProductGid && window.globalProductData && window.globalProductData.getProduct
        ? window.globalProductData.getProduct(swatchProductGid)
        : null;

    if (!rawNode) {
      doppLog(`swatch click: no cached product data for ${swatchProductGid} — falling back to regular price.`, card);
      return;
    }

    const doppProduct = toDoppProductFromGlobalProductData(rawNode);
    if (!doppProduct) return;

    const api = await waitForDoppApi(DOPP_INIT_TIMEOUT_MS);
    if (!api) return;

    try {
      const result = await api.calculateDiscountedPrices(doppProduct);
      card.dataset[PROCESSED_ATTR] = 'true';
      applyResult(card, doppProduct, result);
    } catch (error) {
      doppWarn(`swatch click: calculateDiscountedPrices rejected for product ${doppProduct.productId}.`, error);
    }
  }, 300);
});

// Theme-agnostic fallback for surfaces that append cards without dispatching the event above
// (e.g. a carousel library that clones/rebuilds slides internally).
if (window.MutationObserver) {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.addedNodes.length > 0) {
        void enhanceAll(document);
        return;
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}
